import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, ChevronDown } from "lucide-react";
import { AUDIO_SPEEDS, useLanguage } from "@shared/contexts/LanguageContext";

const NewAudioPlayer = ({ audioUrl }) => {
  const { audioSpeed, setAudioSpeed, t } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);

  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const animationFrameRef = useRef();
  const isDraggingRef = useRef(false);

  // Shared with the Preferences panel — one option set (review #4).
  const speedOptions = AUDIO_SPEEDS.map((value) => ({
    value,
    label: `${value}x`,
  }));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };

    const setAudioTime = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleSeeking = () => setIsSeeking(true);
    const handleSeeked = () => {
      setIsSeeking(false);
      if (isDraggingRef.current === false && isPlaying) {
        audio
          .play()
          .catch((e) => console.error("Error resuming play after seek:", e));
      }
    };

    audio.addEventListener("loadeddata", setAudioData);
    audio.addEventListener("timeupdate", setAudioTime);
    audio.addEventListener("ended", () => setIsPlaying(false));
    audio.addEventListener("seeking", handleSeeking);
    audio.addEventListener("seeked", handleSeeked);

    return () => {
      audio.removeEventListener("loadeddata", setAudioData);
      audio.removeEventListener("timeupdate", setAudioTime);
      audio.removeEventListener("ended", () => setIsPlaying(false));
      audio.removeEventListener("seeking", handleSeeking);
      audio.removeEventListener("seeked", handleSeeked);
    };
  }, [audioUrl, isSeeking, isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (!isDraggingRef.current) {
        setCurrentTime(audio.currentTime);
      }
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } else {
      cancelAnimationFrame(animationFrameRef.current);
    }

    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = audioSpeed;
    }
  }, [audioSpeed]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((e) => console.error("Error playing audio:", e));
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    if (!progressRef.current || !audioRef.current || !isFinite(duration))
      return;

    const rect = progressRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;

    if (isFinite(newTime)) {
      setCurrentTime(newTime); // Update UI immediately
      audioRef.current.currentTime = newTime;
    }
  };

  const handleMouseDown = (e) => {
    isDraggingRef.current = true;
    handleSeek(e);
  };

  const handleMouseMove = (e) => {
    if (isDraggingRef.current) {
      handleSeek(e);
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    if (isPlaying) {
      audioRef.current
        ?.play()
        .catch((e) => console.error("Error playing on mouse up:", e));
    }
  };

  useEffect(() => {
    const progressNode = progressRef.current;
    if (!progressNode) return;

    // Mouse events
    progressNode.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    // Touch events
    progressNode.addEventListener("touchstart", handleMouseDown, {
      passive: true,
    });
    document.addEventListener("touchmove", handleMouseMove, { passive: true });
    document.addEventListener("touchend", handleMouseUp);

    return () => {
      progressNode.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      progressNode.removeEventListener("touchstart", handleMouseDown);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [duration, isPlaying]);

  const handleSpeedChange = (newSpeed) => {
    setAudioSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
    setShowSpeedOptions(false);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) {
      return "0:00";
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  // Off-list values can't normally occur (the context snaps stored
  // values to AUDIO_SPEEDS) — but if one does, show it rather than
  // mislabeling the playback as 1x.
  const currentSpeedLabel =
    speedOptions.find((s) => s.value === audioSpeed)?.label || `${audioSpeed}x`;

  if (!audioUrl) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 w-full">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        playsInline
      ></audio>

      <button
        onClick={togglePlay}
        className="min-w-[72px] h-14 rounded-full bg-white flex items-center justify-center gap-2 px-3 flex-shrink-0 hover:bg-white/90 transition-colors"
        aria-pressed={isPlaying}
        data-testid="audio-play-button"
      >
        <span className="text-sm font-medium text-black">
          {isPlaying
            ? t({ en: "Pause", tc: "暫停", sc: "暂停" })
            : t({ en: "Play", tc: "播放", sc: "播放" })}
        </span>
        {isPlaying ? (
          <Pause className="w-7 h-7 text-black" />
        ) : (
          <Play className="w-7 h-7 text-black ml-0.5" />
        )}
      </button>

      <div className="flex-1 space-y-0.5">
        <div
          ref={progressRef}
          className="h-2 bg-white/20 rounded-full cursor-pointer relative"
          data-testid="audio-progress-bar"
        >
          <div
            className="h-full bg-white rounded-full relative"
            style={{ width: `${progressPercentage}%` }}
          >
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg border border-black/20"
              style={{ transform: "translate(50%, -50%)" }}
            />
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-white/60">
          <span>{formatTime(currentTime)}</span>
          <span>-{formatTime(duration - currentTime)}</span>
        </div>
      </div>

      <div className="relative flex-shrink-0">
        <button
          onClick={() => setShowSpeedOptions(!showSpeedOptions)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
          data-testid="speed-selector"
        >
          {currentSpeedLabel}
          <ChevronDown
            className={`w-3 h-3 transition-transform ${showSpeedOptions ? "rotate-180" : ""}`}
          />
        </button>

        {showSpeedOptions && (
          <div className="absolute bottom-full mb-1 right-0 bg-zinc-800 rounded-lg shadow-lg border border-white/20 overflow-hidden z-10">
            {speedOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSpeedChange(option.value)}
                className={`block w-full px-4 py-2 text-sm text-left transition-colors ${
                  audioSpeed === option.value
                    ? "bg-white text-black"
                    : "text-white hover:bg-white/10"
                }`}
                data-testid={`speed-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewAudioPlayer;
